import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { apiFetch } from '../../api/client.js';
import { useDecideValidation } from '../../api/validations.js';
import type { Invoice, Tenant, Validation } from '../../api/types.js';
import { formatAmount, formatInvoiceDate } from '../invoices/format.js';
import { resolveDueDate } from '../invoices/dueDate.js';
import { StateBadge } from '../invoices/StateBadge.js';
import { DecideModal } from '../validations/DecideModal.js';
import { reminderText } from './AssignedToMeWidget.js';
import { copyToClipboard } from '../../lib/clipboard.js';
import { EmptyState } from '../../components/EmptyState.js';

interface Props {
  validations: Validation[];
  tenants: Tenant[];
}

function tenantShort(tenants: Tenant[], tenantId: string): string {
  const t = tenants.find((x) => x.tenantId === tenantId);
  if (!t) return tenantId.slice(0, 8);
  const n = t.legalName;
  if (/altyor industrie/i.test(n)) return 'AIF';
  if (/altyor holding/i.test(n)) return 'Holding';
  return n;
}

export function AwaitingValidationWidget({ validations, tenants }: Props) {
  const decideMutation = useDecideValidation();
  const [active, setActive] = useState<Validation | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  // Fan-out invoice fetches per validation primaryKey so each row can
  // show supplier / amount / state. Contract Validation does not inline
  // these fields, so we hydrate from /api/invoices/{primaryKey}.
  const invoiceQueries = useQueries({
    queries: validations.map((v) => ({
      queryKey: ['invoice', v.primaryKey] as const,
      queryFn: () => apiFetch<Invoice>(`/invoices/${encodeURIComponent(v.primaryKey)}`),
      staleTime: 30_000,
    })),
  });

  const rows = validations
    .map((v, i) => ({ v, invoice: invoiceQueries[i]?.data ?? null }))
    .sort((a, b) => compareDue(a.invoice?.dueDate ?? null, b.invoice?.dueDate ?? null));

  async function handleDecide(payload: { decision: 'accept' | 'refuse'; comment: string | null }) {
    if (!active) return;
    try {
      await decideMutation.mutateAsync({ validationId: active.validationId, payload });
      setActive(null);
    } catch {
      /* keep modal open; error visible via decideMutation.isError if surfaced upstream */
    }
  }

  async function copyReminder(invoice: Invoice) {
    const due = resolveDueDate(invoice.dueDate, invoice.invoiceDate ?? null);
    const dueDisplay = due.iso ? formatInvoiceDate(due.iso) : '—';
    const text = reminderText(invoice, dueDisplay, due.isEstimated);
    try {
      await copyToClipboard(text);
      setCopied(invoice.primaryKey);
      window.setTimeout(() => setCopied((c) => (c === invoice.primaryKey ? null : c)), 1500);
    } catch {
      setCopied(null);
    }
  }

  return (
    <section className="dashboard-widget" data-testid="widget-validation">
      <header className="dashboard-widget__head">
        <h2 className="display dashboard-widget__title">En attente de validation</h2>
        <span className="dashboard-widget__count" data-testid="widget-validation-count">
          {validations.length}
        </span>
      </header>
      {validations.length === 0 ? (
        <EmptyState
          testId="widget-validation-empty"
          variant="inline"
          icon="🕓"
          title="Aucune validation demandée"
          description="Aucune facture n'attend votre approbation."
        />
      ) : (
        <ul className="dashboard-rows" data-testid="widget-validation-list">
          {rows.map(({ v, invoice }) => {
            const due = invoice
              ? resolveDueDate(invoice.dueDate, invoice.invoiceDate ?? null)
              : { iso: null, isEstimated: false };
            const dueDisplay = due.iso ? formatInvoiceDate(due.iso) : '—';
            return (
              <li
                key={v.validationId}
                className="dashboard-row"
                data-testid={`validation-row-${v.validationId}`}
              >
                <span className="pill pill--neutral dashboard-row__tenant">
                  {tenantShort(tenants, v.tenantId)}
                </span>
                <Link
                  to={`/t/${v.tenantId}/invoices/${encodeURIComponent(v.primaryKey)}`}
                  className="dashboard-row__main"
                >
                  <span className="dashboard-row__supplier">{invoice?.sender?.name ?? '—'}</span>
                  <span className="muted">
                    {invoice?.invoiceNumber ?? v.primaryKey.split('__').slice(-1)[0]}
                  </span>
                </Link>
                <span className="dashboard-row__amount">
                  {invoice
                    ? formatAmount(invoice.amountTotal ?? null, invoice.currency ?? null)
                    : '—'}
                </span>
                {invoice ? (
                  <StateBadge state={invoice.state} dispute={invoice.dispute ?? null} />
                ) : (
                  <span className="pill pill--info">…</span>
                )}
                <span className="dashboard-row__due">
                  {dueDisplay}
                  {due.isEstimated ? (
                    <span className="pill pill--neutral dashboard-row__estimated">estimée</span>
                  ) : null}
                </span>
                <div className="dashboard-row__actions">
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => invoice && void copyReminder(invoice)}
                    disabled={!invoice}
                    data-testid={`validation-row-${v.validationId}-copy`}
                  >
                    {copied === invoice?.primaryKey ? 'Copié !' : 'Copier rappel Teams'}
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => setActive(v)}
                    data-testid={`validation-row-${v.validationId}-validate`}
                  >
                    Valider
                  </button>
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => setActive(v)}
                    data-testid={`validation-row-${v.validationId}-refuse`}
                  >
                    Refuser
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {active ? (
        <DecideModal
          validation={active}
          busy={decideMutation.isPending}
          onDecide={handleDecide}
          onClose={() => setActive(null)}
        />
      ) : null}
    </section>
  );
}

function compareDue(a: string | null, b: string | null): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.localeCompare(b);
}
